import React from 'react';
import { FlightDetailsLegTable } from 'acscp/components/legs/flightDetailsLegTable';
import { QuoteTextSections } from 'acscp/components/quotes/quoteDetails';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import style from './quoteAccept.module.scss';
import { Quote } from 'acscp/store/quotesState';
import { PageTitle } from 'acscp/components/common/pageTitle';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';
import { Background, Row } from 'acscp/components/common/row';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import { GoBackLink } from 'acscp/components/common/goBackLink';
import { ChangeEvent } from 'react';
import { LabeledCheckbox } from '../common/labeledCheckbox';
import { FormValidator } from '../common/helpers/formValidator';
import { BooleanValidationChecks } from '../common/helpers/validationChecks';
import { ErrorsDisplay } from '../common/errorsDisplay';
import classNames from 'classnames';

const messages = defineMessages({
    readAndUnderstoodError: {
        id: 'components.quotes.quoteAccept.mustReadAndUnderstand',
        defaultMessage: 'Please confirm that you have read and understood this notice.'
    }
});

type QuoteAcceptProps = InjectedIntlProps & {
    quote: Quote;
    bookingId: string;
    acceptQuote: (notes: string) => void;
    acceptRequestIsLoading: boolean;
    jetCardNo: string | null;
    jetCardType: string | null;
};

interface QuoteAcceptForm {
    notesValue: string;
    readAndUnderstoodIsChecked: boolean;
}

type QuoteAcceptState = QuoteAcceptForm & {
    showErrors: boolean;
};

class QuoteAcceptPresentational extends React.Component<QuoteAcceptProps, QuoteAcceptState> {
    constructor(props: QuoteAcceptProps) {
        super(props);

        const translator = this.props.intl.formatMessage;

        this.state = {
            notesValue: this.props.quote.userAcceptanceNotes || '',
            readAndUnderstoodIsChecked: false,
            showErrors: false
        };

        this.validator = new FormValidator([{
            validatedPropertyName: 'readAndUnderstoodIsChecked',
            validationCheck: BooleanValidationChecks.mustBeTrue(() => this.state.readAndUnderstoodIsChecked),
            errorMessage: translator(messages.readAndUnderstoodError)
        }]);

        this.handleUserNotesChange = this.handleUserNotesChange.bind(this);
    }

    private validator: FormValidator;

    private toggleReadAndUnderstood = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({readAndUnderstoodIsChecked: event.target.checked});
    }

    private isStateValid(): boolean {
        return !this.props.jetCardNo || this.validator.isStateValid();
    }

    private handleUserNotesChange(event: ChangeEvent<HTMLTextAreaElement>): void {
        this.setState({notesValue: event.target.value});
    }

    render(): JSX.Element {
        const {quote, bookingId, acceptQuote, acceptRequestIsLoading, jetCardNo, jetCardType} = this.props;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, undefined);
        const errorMessages = (validationErrors.readAndUnderstoodIsChecked || []).map(err => err.message);
        const highlightError = validationErrors.readAndUnderstoodIsChecked !== undefined;
        return (
            <div className={style.QuoteAccept}>
                <PageTitle
                    goBackAddress={`/trip/${bookingId}`}
                    title={
                        <FormattedMessage
                            id={'components.quotes.quoteAccept.acceptQuoteTitle'}
                            defaultMessage={'ACCEPT QUOTE {quoteNumber}'}
                            values={{quoteNumber: quote.order}}
                        />
                    }
                />
                <Row className={style.ReviewSummary}>
                    <FormattedMessage
                        id={'components.quotes.quoteAccept.reviewSummaryTitle'}
                        defaultMessage={'REVIEW SUMMARY'}
                    />
                </Row>
                <Row background={Background.Grey} className={style.AcceptNotice}>
                    {!!jetCardNo
                    ? <FormattedMessage
                        id={`components.quotes.quoteAccept.bookNoticeReview`}
                        defaultMessage={`Please review this information and, if you are happy with ` +
                            `it, click Confirm and Book.`}
                    />
                    : <FormattedMessage
                        id={`components.quotes.quoteAccept.acceptNoticeReview`}
                        defaultMessage={`Please review this information and, if you are happy with ` +
                            `it, click Confirm and Accept.`}
                    />}
                    {!jetCardNo &&
                        <FormattedMessage
                            id={'components.quotes.quoteAccept.acceptNoticeNotify'}
                            defaultMessage={'This will notify your account manager who will check the ' +
                                'availability of the aircraft then prepare the booking form.'}
                        />
                    }
                </Row>
                <Row className={style.LegsTable}>
                    <FlightDetailsLegTable
                        legs={quote.legs}
                        hasLowerBorder={true}
                        departmentKey={quote.department}
                        quoteType={quote.quoteType}
                    />
                </Row>
                <Row className={style.QuoteDetails}>
                    <QuoteTextSections
                        quote={quote}
                        isQuoteDetailsTab={false}
                        userNotesInputChangeHandler={this.handleUserNotesChange}
                        jetCardHolder={!!jetCardNo}
                        jetCardType={jetCardType}
                    />
                </Row>
                {!!jetCardNo &&
                    <Row className={style.AgreementRow}>
                        <LabeledCheckbox
                            containerClassname={classNames(style.Checkbox)}
                            label={
                                <FormattedMessage
                                    id={'components.quotes.quoteAccept.jetCardAgreementNotice'}
                                    defaultMessage={'We refer to the On Demand Master Agreement ' +
                                    'No. {jetCardNo} ("the Agreement"). The terms and conditions ' +
                                    'contained in the Agreement shall have the same meaning in this ' +
                                    'online Flight Confirmation and as agreed pursuant to your ' +
                                    'Variation Agreement. Please tick this box to confirm that you ' +
                                    'have read and understood this notice.'}
                                    values={{jetCardNo}}
                                />
                            }
                            rightAlignCheckbox={true}
                            darkBox={true}
                            borderedBox={true}
                            highlightError={highlightError}
                            onChange={this.toggleReadAndUnderstood}
                            checked={!!this.state.readAndUnderstoodIsChecked}
                        />
                    </Row>
                }
                {!!jetCardNo &&
                    <Row>
                        <ErrorsDisplay
                            className={style.ErrorDisplay}
                            errors={errorMessages}
                        />
                    </Row>
                }
                <Row background={Background.Grey} className={style.ButtonGroup}>
                    <ButtonWithLoadingIndicator
                        className={style.ConfirmButton}
                        onClick={() => {
                            if (this.isStateValid()) {
                                acceptQuote(this.state.notesValue);
                            }
                            this.setState({ showErrors: true });
                        }}
                        isLoading={acceptRequestIsLoading}
                    >
                        {!!jetCardNo
                            ? <FormattedMessage
                                id={'components.quotes.quoteAccept.bookButton'}
                                defaultMessage={'CONFIRM AND BOOK'}
                            />
                            : <FormattedMessage
                                id={'components.quotes.quoteAccept.acceptButton'}
                                defaultMessage={'CONFIRM AND ACCEPT'}
                            />
                        }
                    </ButtonWithLoadingIndicator>
                    <GoBackLink className={style.BackButton} fallbackAddress={`/trip/${bookingId}`}>
                        <ArrowIcon/> <FormattedMessage
                            id={'components.quotes.quoteAccept.backButton'}
                            defaultMessage={'BACK'}
                        />
                    </GoBackLink>
                </Row>
            </div>
        );
    }
}

const QuoteAccept = injectIntl(QuoteAcceptPresentational);
export {QuoteAccept};
